import React, { useState } from 'react';
import { DocumentArrowUpIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { payerService } from '../../services/payerService';

const HEADER_WORDS = new Set(['name', 'payer', 'payer name', 'payername', 'identifier', 'npi', 'edi payer id']);

/**
 * Parses a simple CSV/TXT upload into bulk-import rows.
 * Expected shape: one payer per line, optionally "Payer Name,Identifier".
 * A first line that looks like a header (e.g. "Payer Name,Identifier") is skipped.
 */
function parsePayerListFile(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];

  const firstCols = lines[0].split(',').map((c) => c.trim().toLowerCase());
  const looksLikeHeader = firstCols.every((c) => HEADER_WORDS.has(c));
  const dataLines = looksLikeHeader ? lines.slice(1) : lines;

  return dataLines
    .map((line) => {
      const [name, identifier] = line.split(',').map((c) => c.trim());
      if (!name) return null;
      return identifier ? { name, identifier } : { name };
    })
    .filter(Boolean);
}

export default function PayerListUploadPanel({ onImported }) {
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError('');
    setResult(null);

    try {
      const text = await file.text();
      const rows = parsePayerListFile(text);
      if (rows.length === 0) {
        setError('No payer names found in that file. Expect one payer per line (optionally "Payer Name,Identifier").');
        return;
      }
      setImporting(true);
      const response = await payerService.bulkImportOrganizationPayers(rows);
      setResult(response);
      if (onImported) onImported(response);
    } catch (err) {
      setError(err.message || 'Failed to import payer list.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-1">
        <DocumentArrowUpIcon className="w-5 h-5 text-blue-600" />
        Upload your payer list
      </h3>
      <p className="text-xs text-gray-600 mb-3">
        Optional — upload a CSV or text file with the payers you work with (one per line, or
        "Payer Name,Identifier"). We'll match them against our payer catalog and add any new
        ones to your organization's list.
      </p>

      <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
        <DocumentArrowUpIcon className="w-4 h-4" />
        {fileName || 'Choose file'}
        <input
          type="file"
          accept=".csv,.txt"
          className="hidden"
          onChange={handleFileChange}
          disabled={importing}
        />
      </label>

      {importing && <p className="text-xs text-gray-500 mt-2">Importing payer list…</p>}

      {error && (
        <div className="mt-3 flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
          <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-3 flex items-start gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Imported {result.results.length} payer{result.results.length === 1 ? '' : 's'}:{' '}
            {result.added_count} matched, {result.created_count} added as new
            {result.error_count > 0 ? `, ${result.error_count} skipped` : ''}.
          </span>
        </div>
      )}
    </div>
  );
}
